import { createPayloadAction, createRequestActionCreators, RequestAction } from 'helpers/actionsHelpers'
import { CategoryId } from 'interfaces/meta'
import { PayloadAction } from 'interfaces/action'
import { Timeframe } from 'interfaces/chart'
import { TimeframeTyped } from 'interfaces/global'
import { PeergroupAccountsBreakdownCollection, PeergroupsTyped } from 'interfaces/peergroups'
import { AccountCollection, AccountRateCollection, AccountType, AccountTyped, AllDetailedAccountsDict } from 'interfaces/accounts'
import { PeergroupTrendCollection, Trend } from 'interfaces/trend'

export type AccountsPayload<TData> = AccountTyped & {
  data: TData
}

export type AccountTrendPayload<TTrend> = AccountTyped & {
  category: CategoryId
  data: TTrend
}

export type AccountMemberTrendPayload<TTrend> = AccountTyped & {
  category: CategoryId
  timeframe: Timeframe
  data: TTrend
}

type AccountBreakdownPayload = {
  type: AccountType
  date: string
}

type SetMemberAccountBreakdownPayload = AccountBreakdownPayload & {
  data: AccountCollection
}

type SetPeergroupAccountBreakdownPayload = AccountBreakdownPayload & {
  data: PeergroupAccountsBreakdownCollection
}

type GetPeergroupAccountTrendsPayload = {
  type: AccountType
  category: CategoryId
  peergroups: number[]
}

type GetMemberAccountTrendsPayload = {
  type: AccountType
  timeframe: Timeframe
  category: CategoryId
}

// ===========================================================================//
//                                  UNIFIED                                   //
// ===========================================================================//

export const GET_MEMBER_ALL_ACCOUNTS = 'GET_MEMBER_ALL_ACCOUNTS' as const
type GetMemberAllAccounts = RequestAction<typeof GET_MEMBER_ALL_ACCOUNTS, { type: AccountType }, AllDetailedAccountsDict>
export const getMemberAllAccountsActionCreator = createRequestActionCreators<typeof GET_MEMBER_ALL_ACCOUNTS, { type: AccountType }, AllDetailedAccountsDict>(GET_MEMBER_ALL_ACCOUNTS)

// ===========================================================================//
//                                 BREAKDOWNS                                 //
// ===========================================================================//

export const GET_MEMBER_ACCOUNT_BREAKDOWN = 'GET_MEMBER_ACCOUNT_BREAKDOWN' as const
type GetMemberAccountBreakdown = PayloadAction<typeof GET_MEMBER_ACCOUNT_BREAKDOWN, AccountBreakdownPayload>
export const getMemberAccountBreakdownActionCreator = (type: AccountType, date: string): GetMemberAccountBreakdown =>
  createPayloadAction(GET_MEMBER_ACCOUNT_BREAKDOWN, { type, date })

export const SET_MEMBER_ACCOUNT_BREAKDOWN = 'SET_MEMBER_ACCOUNT_BREAKDOWN' as const
type SetMemberAccountBreakdown = PayloadAction<typeof SET_MEMBER_ACCOUNT_BREAKDOWN, SetMemberAccountBreakdownPayload>
export const setMemberAccountBreakdownActionCreator = (type: AccountType, date: string, data: AccountCollection): SetMemberAccountBreakdown =>
  createPayloadAction(SET_MEMBER_ACCOUNT_BREAKDOWN, { data, date, type })

export const GET_PEER_GROUP_ACCOUNT_BREAKDOWN = 'GET_PEER_GROUP_ACCOUNT_BREAKDOWN' as const
type GetPeergroupAccountBreakdown = PayloadAction<typeof GET_PEER_GROUP_ACCOUNT_BREAKDOWN, AccountBreakdownPayload & PeergroupsTyped>
export const getPeergroupAccountBreakdownActionCreator = (type: AccountType, date: string, peergroups: number[]): GetPeergroupAccountBreakdown =>
  createPayloadAction(GET_PEER_GROUP_ACCOUNT_BREAKDOWN, { type, date, peergroups })

export const SET_PEER_GROUP_ACCOUNT_BREAKDOWN = 'SET_PEER_GROUP_ACCOUNT_BREAKDOWN' as const
type SetPeergroupAccountBreakdown = PayloadAction<typeof SET_PEER_GROUP_ACCOUNT_BREAKDOWN, SetPeergroupAccountBreakdownPayload>
export const setPeergroupAccountBreakdownActionCreator = (type: AccountType, date: string, data: PeergroupAccountsBreakdownCollection): SetPeergroupAccountBreakdown =>
  createPayloadAction(SET_PEER_GROUP_ACCOUNT_BREAKDOWN, { type, date, data })


// ===========================================================================//
//                                   RATES                                    //
// ===========================================================================//

export const GET_ACCOUNT_RATES = 'GET_ACCOUNT_RATES' as const
type GetAccountRates = PayloadAction<typeof GET_ACCOUNT_RATES, AccountTyped & TimeframeTyped & { peersId: number }>
export const getAccountRatesActionCreator = (type: AccountType, timeframe: Timeframe, peersId: number): GetAccountRates =>
  createPayloadAction(GET_ACCOUNT_RATES, { type, timeframe, peersId })

export const SET_ACCOUNT_RATES = 'SET_ACCOUNT_RATES' as const
type SetAccountRates = PayloadAction<typeof SET_ACCOUNT_RATES, AccountsPayload<AccountRateCollection> & TimeframeTyped>
export const setAccountRatesActionCreator = (data: AccountRateCollection, type: AccountType, timeframe: Timeframe): SetAccountRates =>
  createPayloadAction(SET_ACCOUNT_RATES, { data, type, timeframe })


// ===========================================================================//
//                                   TRENDS                                   //
// ===========================================================================//

export const GET_MEMBER_ACCOUNT_TRENDS = 'GET_MEMBER_ACCOUNT_TRENDS' as const
type GetMemberAccountTrends = PayloadAction<typeof GET_MEMBER_ACCOUNT_TRENDS, GetMemberAccountTrendsPayload>
export const getMemberAccountTrendsActionCreator = (type: AccountType, timeframe: Timeframe, category: CategoryId): GetMemberAccountTrends =>
  createPayloadAction(GET_MEMBER_ACCOUNT_TRENDS, { type, timeframe, category })

export const SET_MEMBER_ACCOUNT_TRENDS = 'SET_MEMBER_ACCOUNT_TRENDS' as const
type SetMemberAccountTrends = PayloadAction<typeof SET_MEMBER_ACCOUNT_TRENDS, AccountMemberTrendPayload<Trend>>
export const setMemberAccountTrendsActionCreator = (type: AccountType, timeframe: Timeframe, category: CategoryId, data: Trend): SetMemberAccountTrends =>
  createPayloadAction(SET_MEMBER_ACCOUNT_TRENDS, { type, category, timeframe, data })

export const GET_PEER_GROUP_ACCOUNT_TRENDS = 'GET_PEER_GROUP_ACCOUNT_TRENDS' as const
type GetPeergroupAccountTrends = PayloadAction<typeof GET_PEER_GROUP_ACCOUNT_TRENDS, GetPeergroupAccountTrendsPayload>
export const getPeergroupAccountTrendsActionCreator = (type: AccountType, category: CategoryId, peergroups: number[]): GetPeergroupAccountTrends =>
  createPayloadAction(GET_PEER_GROUP_ACCOUNT_TRENDS, { type, category, peergroups })

export const SET_PEER_GROUP_ACCOUNT_TRENDS = 'SET_PEER_GROUP_ACCOUNT_TRENDS' as const
type SetPeergroupAccountTrends = PayloadAction<typeof SET_PEER_GROUP_ACCOUNT_TRENDS, AccountTrendPayload<PeergroupTrendCollection>>
export const setPeergroupAccountTrendsActionCreator = (type: AccountType, category: CategoryId, data: PeergroupTrendCollection): SetPeergroupAccountTrends =>
  createPayloadAction(SET_PEER_GROUP_ACCOUNT_TRENDS, { type, category, data })


type Action =
  | GetMemberAllAccounts
  | GetMemberAccountBreakdown
  | SetMemberAccountBreakdown
  | GetPeergroupAccountBreakdown
  | SetPeergroupAccountBreakdown
  | GetAccountRates
  | SetAccountRates
  | GetMemberAccountTrends
  | SetMemberAccountTrends
  | GetPeergroupAccountTrends
  | SetPeergroupAccountTrends


export default Action
